import {DecisionGraph, buildDecisionGraph} from "../decision-graph/DecisionGraph";
import type {DecisionGraphModel} from "../decision-graph/decision-graph-model";
import {billingEntityName, billingDecisionInputs, billingOutputLabel} from "./billing-decision-display";
import type {RichMessage} from "../diagnostics/types";
import type {BillingDecisionTrace} from "./ticket-cost-types";

const routingCodes = new Set(["TICKET_CODES", "PRODUCT_SITE_AFE", "CONTAINER_AFE", "CLUSTER_AFE",
  "EXPLICIT_WELL", "CONTAINER_SITE", "CONTAINER_WELLS", "SITE_FALLBACK", "PARENT_SITE_AFE"]);
const candidateCodes = new Set(["STAGE_SHUT_IN", "STAGE_FREIGHT", "STAGE_SERVICE", "STAGE_INTERVAL", "STAGE_MISSING",
  "MATCHED_STAGE", "DEFAULT_STAGE", "WELL_EXCLUDED"]);


/** Branches end at excluded candidates; only selected candidates connect to a billing result. */
export const buildBillingDecisionGraph = (trace: BillingDecisionTrace): DecisionGraphModel => {
  const outputs = trace.steps.filter(step => step.code === "BILLING_LOCATION");
  const routes = trace.steps.filter(step => routingCodes.has(step.code));
  const candidates = new Map<string, typeof trace.steps>();
  for (const step of trace.steps) {
    if (!step.entityId || !candidateCodes.has(step.code)) continue;
    candidates.set(step.entityId, [...(candidates.get(step.entityId) ?? []), step]);
  }
  const nodes: DecisionGraphModel["nodes"] = [];
  const edges: DecisionGraphModel["edges"] = [];
  const addNode = (id: string, title: string, lines: string[], tone: "neutral" | "success" | "danger" = "neutral", messages?: RichMessage[]) => {
    const kind = id === "inputs" ? "input" : id === "rule-checks" ? "reason" : id.startsWith("route-") ? "rule" : id.startsWith("candidate-") ? "candidate" : "output";
    nodes.push({id, kind, title, lines, messages, status: tone === "success" ? "selected" : tone === "danger" ? "rejected" : "neutral"});
  };
  const connect = (source: string, target: string, rejected = false) => edges.push({source, target, status: rejected ? "rejected" : "selected"});
  const inputs = billingDecisionInputs(trace);
  addNode("inputs", "Ticket inputs", inputs.length ? inputs.map(({label, value}) => `${label}: ${value}`) : ["No location inputs provided."]);
  let parent = "inputs";
  if (routes.length) {
    const checks = trace.steps.filter(step => step.code === "RULE_CHECK");
    const reasons = checks.length ? checks.map(check => check.explanation)
      : [...routes.map(route => route.explanation), "Earlier rule checks were not included in this explanation."];
    addNode("rule-checks", "Why this rule?", reasons, "neutral", checks.some(check => check.message)
      ? checks.map(check => check.message ?? {summary: check.explanation}) : undefined);
    connect(parent, "rule-checks");
    parent = "rule-checks";
  }
  if (routes.length) {
    addNode("route-0", "Location rule", routes.map(route => ({
      TICKET_CODES: "Use ticket coding", PRODUCT_SITE_AFE: "Use the product's site expenditure code", CONTAINER_AFE: "Use the container expenditure code",
      CLUSTER_AFE: "Use the cluster expenditure code", EXPLICIT_WELL: "Use the ticket's well", CONTAINER_SITE: "Use the container's site",
      CONTAINER_WELLS: "Evaluate the container's associated wells", SITE_FALLBACK: "Use the site fallback", PARENT_SITE_AFE: "Use the parent site expenditure code",
    }[route.code] ?? route.explanation)), "neutral", routes.some(route => route.message)
      ? routes.map(route => route.message ?? {summary: route.explanation}) : undefined);
    connect(parent, "route-0");
    parent = "route-0";
  }
  const candidateIds = new Map<string, string>();
  [...candidates].forEach(([id, steps], index) => {
    const selected = outputs.some(output => output.entityId === id);
    const excluded = steps.some(step => step.code === "WELL_EXCLUDED");
    const reasons = steps.filter(step => step.code !== "WELL_EXCLUDED");
    const nodeId = `candidate-${index}`;
    addNode(nodeId, selected ? "Selected well" : excluded ? "Excluded well" : "Evaluated well",
      [billingEntityName(trace, id, "Well name unavailable"), ...(reasons.length ? reasons : steps).map(step => step.explanation)], selected ? "success" : excluded ? "danger" : "neutral",
      (reasons.length ? reasons : steps).some(step => step.message) ? [{summary: billingEntityName(trace, id, "Well name unavailable")},
        ...(reasons.length ? reasons : steps).map(step => step.message ?? {summary: step.explanation})] : undefined);
    connect(parent, nodeId, !selected);
    candidateIds.set(id, nodeId);
  });
  if (outputs.length) outputs.forEach((output, index) => {
    const candidate = output.entityId ? candidateIds.get(output.entityId) : undefined;
    const coded = trace.steps.find(step => step.code === "CODED_LOCATION" && step.entityId === output.entityId);
    addNode(`result-${index}`,
      "Location mapping", [billingOutputLabel(trace, output), ...(output.values.afe ? [`expenditure code: ${output.values.afe}`] : []), ...(coded ? [coded.explanation] : [])], "success", output.message ? [output.message,
        ...(output.values.afe ? [{summary: `expenditure code: ${output.values.afe}`}] : []),
        ...(coded ? [coded.message ?? {summary: coded.explanation}] : [])] : undefined);
    connect(candidate ?? parent, `result-${index}`);
  });
  else {
    addNode("no-result", trace.steps.length ? "No eligible billing location" : "No billing decision available", [], "neutral");
    if (!candidates.size) connect(parent, "no-result");
    else candidateIds.forEach(id => connect(id, "no-result", true));
  }
  const finalOutput = trace.steps.find(step => step.code === "FINAL_BILLING_LOCATION");
  if (finalOutput) {
    addNode("final-output", "Final billing location", [finalOutput.explanation,
      ...(finalOutput.values.afe ? [`Expenditure code: ${finalOutput.values.afe}`] : [])],
      finalOutput.outcome === "selected" ? "success" : "neutral");
    if (outputs.length) outputs.forEach((_, index) => connect(`result-${index}`, "final-output"));
    else connect("no-result", "final-output");
  }
  return {nodes, edges};
};

/** Compatibility helper for consumers inspecting the rendered billing graph. */
export const buildBillingDecisionFlow = (trace: BillingDecisionTrace, compact = false) => buildDecisionGraph(buildBillingDecisionGraph(trace), compact);

export const BillingDecisionFlow = ({trace}: {trace: BillingDecisionTrace}) => <DecisionGraph
  model={buildBillingDecisionGraph(trace)} label="Billing decision flow"
  description="Follow the arrows from the ticket to the billing result. Dashed branches show candidates that were not selected. Pan or zoom to inspect the diagram." />;
